/**
 * 세션 (Todak stores/session.js 처럼 zustand + persist).
 *
 * status
 *   'checking' 저장된 토큰으로 /auth/me 를 묻는 중. 이 동안은 화면을 고르지 않습니다
 *   'guest'    로그인 전
 *   'member'   로그인함. user.onboardingStep 으로 첫 방문 어디쯤인지 압니다 (done 이면 단골)
 *   'error'    서버에 닿지 않아 확인하지 못함. 토큰은 지우지 않고 다시 시도를 보여 줍니다
 *
 * 토큰만 저장하고 user 는 매번 서버에서 받습니다. 저장해 둔 user 로 화면을 고르면
 * 서버에서 지워진 계정으로도 화면이 열립니다.
 *
 * **한 번 확인한 세션은 다시 묻지 않습니다** (히어로챗 D109). 화면을 오갈 때마다 확인하면
 * 그동안 로그인 화면이 한 프레임 비쳐 깜빡입니다.
 */
import { forgetDeviceToken } from '../lib/push.js';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { configureApi } from '../api/client.js';
import * as authApi from '../api/auth.js';

let restoreInFlight = null;

export const useSession = create(
    persist(
        (set, get) => ({
            token: null,
            user: null,
            status: 'checking',

            setSession({ token, user }) {
                set({ token, user, status: 'member' });
            },

            setUser(user) {
                set({ user });
            },

            clearSession() {
                set({ token: null, user: null, status: 'guest' });
            },

            /**
             * 앱이 뜰 때 한 번. 토큰이 없으면 묻지 않고 guest 입니다.
             * 개발 중 StrictMode 가 효과를 두 번 돌려도 묻는 요청은 하나만 나갑니다.
             */
            restore() {
                if (!restoreInFlight) {
                    restoreInFlight = get()
                        .restoreOnce()
                        .finally(() => {
                            restoreInFlight = null;
                        });
                }
                return restoreInFlight;
            },

            async restoreOnce() {
                const { token } = get();
                if (!token) {
                    set({ status: 'guest' });
                    return;
                }
                set({ status: 'checking' });
                try {
                    const { user } = await authApi.fetchMe();
                    set({ user, status: 'member' });
                } catch (error) {
                    if (error?.status === 401 || error?.status === 404) {
                        get().clearSession();
                        return;
                    }
                    // 네트워크나 서버 오류. 토큰은 버리지 않고 화면이 다시 시도할 수 있게 둡니다.
                    console.error('[session] 세션 확인 실패', error?.code ?? error?.name);
                    set({ status: 'error' });
                }
            },

            async logout() {
                // 앱이면 이 기기 푸시 토큰부터 지움(DEV-12). 세션이 살아 있을 때 해야 함
                await forgetDeviceToken();
                try {
                    await authApi.logout();
                } catch (error) {
                    // 서버 세션을 못 지워도 이 기기에서는 로그아웃합니다. 서버 세션은 만료로 사라집니다.
                    console.error('[session] 서버 로그아웃 실패', error?.code ?? error?.name);
                }
                get().clearSession();
            },
        }),
        {
            name: 'byeolil-session',
            storage: createJSONStorage(() => localStorage),
            partialize: (state) => ({ token: state.token }),
        }
    )
);

configureApi({
    tokenGetter: () => useSession.getState().token,
    authErrorHandler: () => {
        useSession.getState().clearSession();
    },
});
